import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Edit,
  Trash2,
  User,
  Eye,
  ChevronLeft,
  ChevronRight,
  UserIcon,
  ExternalLinkIcon,
  Download,
} from 'lucide-react';
import {
  selectSelectedSegment,
  selectSegmentsLoading,
  selectSegmentsExportBlob,
  selectSegmentsExportBlobLoading,
} from 'store/segments/segments.selector';
import {
  getSegmentRequest,
  getSegmentContactsRequest,
  exportSegmentContactsRequest,
  clearExportSegmentContactsSuccess,
} from 'store/segments/segments.action';
import Badge from 'components/ui/Badge';
import Button from 'components/ui/Button';
import Select from 'components/ui/Select';
import { formatDate } from 'utils/helper';
import SegmentDeleteModal from 'components/segments/SegmentDeleteModal';
import ConditionDisplay from 'components/segments/ConditionDisplay';
import ActivityTab from 'components/segments/ActivityTab';
import { GetSegmentContactsRequestPayload } from 'types';
import {
  selectContacts,
  selectContactsLoading,
  selectContactsPagination,
} from 'store/segments';
import { ContactTableSkeleton } from 'skeletons';
import { useToast } from 'contexts/ToastContext';

const SegmentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const segment = useSelector(selectSelectedSegment);
  const loading = useSelector(selectSegmentsLoading);
  const contacts = useSelector(selectContacts);
  const contactsLoading = useSelector(selectContactsLoading);
  const contactsPagination = useSelector(selectContactsPagination);
  const exportBlob = useSelector(selectSegmentsExportBlob);
  const exportBlobLoading = useSelector(selectSegmentsExportBlobLoading);
  const [activeTab, setActiveTab] = useState<
    'people' | 'conditions' | 'usage' | 'activity'
  >('people');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getSegmentRequest(id));
    }
  }, [dispatch, id]);

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  useEffect(() => {
    if (segment && id && segment?.contacts?.length > 0) {
      const payload: GetSegmentContactsRequestPayload = {
        id,
        page: 0,
        limit: 10,
      };
      dispatch(getSegmentContactsRequest(payload));
    }
  }, [segment, id, dispatch]);

  // Handle export success
  useEffect(() => {
    if (exportBlob && id) {
      const url = window.URL.createObjectURL(exportBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `segment_${segment?.name || id}_contacts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showToast({
        type: 'success',
        title: 'Export Successful',
        message: 'Segment contacts have been exported successfully.',
      });

      dispatch(clearExportSegmentContactsSuccess());
    }
  }, [exportBlob, id, segment?.name, showToast, dispatch]);

  const handleEdit = () => {
    if (segment?.type === 'Manual') {
      navigate(`/segments/manual/edit/${id}`);
    } else {
      navigate(`/segments/smart/edit/${id}`);
    }
  };

  const handlePageChange = (page: number) => {
    if (segment && id && segment.contacts.length > 0) {
      const payload: GetSegmentContactsRequestPayload = {
        id,
        page,
        limit: contactsPagination.limit,
      };
      dispatch(getSegmentContactsRequest(payload));
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(e.target.value);
    if (
      segment &&
      id &&
      segment.contacts.length > 0 &&
      !Number.isNaN(newLimit)
    ) {
      const payload: GetSegmentContactsRequestPayload = {
        id,
        page: 0,
        limit: newLimit,
      };
      dispatch(getSegmentContactsRequest(payload));
    }
  };

  const PAGE_SIZE_OPTIONS = [
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
  ];

  const handleExportContacts = () => {
    if (id) {
      dispatch(exportSegmentContactsRequest(id));
    }
  };

  // Calculate proper pagination values
  const totalContacts = segment?.contacts?.length || 0;
  const currentPage = contactsPagination.page;
  const limit = contactsPagination.limit;
  const totalPages = Math.ceil(totalContacts / limit);
  const startItem = currentPage * limit + 1;
  const endItem = Math.min((currentPage + 1) * limit, totalContacts);

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!segment) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Segment not found
          </h2>
          <p className="text-gray-600 mb-4">
            The segment you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate('/segments')} variant="outline">
            Back to Segments
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'people', label: 'People', count: segment?.contacts?.length },
    { id: 'conditions', label: 'Conditions' },
    // { id: 'usage', label: 'Usage' },
    { id: 'activity', label: 'Activity' },
  ];

  return (
    <div className="p-6">
      {/* Segment Info */}
      <div className="flex justify-between">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <User className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-semibold text-gray-900">
              {segment.name}
            </h1>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <Badge variant={segment.type === 'Smart' ? 'info' : 'default'}>
              {segment.type}
            </Badge>
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {segment?.contacts?.length} people
            </span>
            <span>Created {formatDate(segment.createdAt)}</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <Button onClick={handleEdit} variant="outline" size="small">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button onClick={handleDelete} variant="danger" size="small">
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="w-full mb-6">
        <div className="inline-flex items-center gap-1 rounded-lg bg-muted/80 px-1.5 py-1 border border-border/50">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer
                      transition-all duration-200 cursor-pointer
                      ${
                        activeTab === tab.id
                          ? 'bg-background text-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                      }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={`py-0.5 px-2 rounded-full text-xs font-medium flex items-center justify-center ${
                    activeTab === tab.id
                      ? 'bg-primary/80 text-primary-foreground'
                      : 'bg-muted-foreground/20 text-muted-foreground'
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="">
        {activeTab === 'people' && (
          <div>
            {segment?.contacts?.length > 0 ? (
              <div>
                {/* Actions Bar */}
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-gray-600">
                    {segment?.contacts?.length} people
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="small"
                      variant="primary"
                      onClick={handleExportContacts}
                      disabled={exportBlobLoading || !segment?.contacts?.length}
                      className="flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      {exportBlobLoading ? 'Exporting...' : 'Export All'}
                    </Button>
                  </div>
                </div>

                {/* Contacts Table */}
                {contactsLoading ? (
                  <ContactTableSkeleton />
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-border bg-card">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border bg-muted/50">
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground w-12">
                            <input
                              type="checkbox"
                              className="rounded border-border accent-primary focus:ring-0"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[200px]">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                            Custom ID
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                            Last Login
                          </th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                            Join Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts?.map((contact, index) => (
                          <tr
                            key={index}
                            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                          >
                            <td className="px-4 py-3 w-12">
                              <input
                                type="checkbox"
                                className="rounded border-border accent-primary focus:ring-0"
                              />
                            </td>
                            <td
                              className="px-4 py-3 whitespace-nowrap min-w-[200px]"
                              onClick={() =>
                                navigate(`/contacts/${contact._id}`)
                              }
                            >
                              <div className="text-sm text-primary hover:text-primary-hover cursor-pointer hover:underline font-medium">
                                {contact.email}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                              <div className="text-sm text-card-foreground font-medium">
                                {contact.firstName} {contact.lastName}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                              <Badge variant="default">
                                {contact.custom_id}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                              <div className="text-sm text-muted-foreground">
                                {formatDate(contact.updatedAt)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                              <div className="text-sm text-muted-foreground">
                                {formatDate(contact.createdAt)}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {!contactsLoading &&
                  contacts.length > 0 &&
                  totalContacts > 0 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-4">
                        <div className="text-sm text-gray-500">
                          {startItem}-{endItem} of {totalContacts}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500 whitespace-nowrap">
                            Per page
                          </span>
                          <Select
                            options={PAGE_SIZE_OPTIONS}
                            value={String(limit)}
                            onChange={handleLimitChange}
                            size="sm"
                            className="w-16 min-w-0"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="small"
                          disabled={currentPage === 0}
                          onClick={() => handlePageChange(currentPage - 1)}
                          className="flex items-center gap-1 px-3 py-1"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>

                        <span className="text-sm text-gray-500 px-3 py-1">
                          Page {currentPage + 1} of {totalPages}
                        </span>

                        <Button
                          variant="outline"
                          size="small"
                          disabled={currentPage >= totalPages - 1}
                          onClick={() => handlePageChange(currentPage + 1)}
                          className="flex items-center gap-1 px-3 py-1"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 ">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-lg font-medium text-gray-900">People</h2>
                  <button className="flex items-center text-primary hover:text-primary-hover text-sm font-medium">
                    View all
                    <ExternalLinkIcon className="w-4 h-4 ml-1" />
                  </button>
                </div>

                {/* Empty State */}
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  {/* User Icon */}
                  <div className="mb-4">
                    <UserIcon className="w-12 h-12 text-gray-400" />
                  </div>

                  {/* No customers found */}
                  <h3 className="text-gray-900 font-medium text-base mb-2">
                    No customers found
                  </h3>

                  {/* Description */}
                  <p className="text-gray-500 text-sm max-w-xs">
                    No customers match the current sample criteria
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'conditions' && (
          <div className="">
            <h3 className="text-lg font-medium mb-4">Segment Conditions</h3>
            <ConditionDisplay conditions={segment.conditions || []} />
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="py-8 text-center">
            <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              Segment usage analytics will be displayed here.
            </p>
          </div>
        )}

        {activeTab === 'activity' && <ActivityTab segment={segment} />}

        {showDeleteModal && (
          <SegmentDeleteModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
          />
        )}
      </div>
    </div>
  );
};

export default SegmentDetails;
